import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
	CURRENT_USER,
	emptyProtoCard,
	seedCanvas,
	seedSomeday,
	type ProtoCard,
} from './seed'
import {
	cloneTree,
	collectAuthors,
	collectLabels,
	depthOf,
	filterSomedayTree,
	findCard,
	flattenWithDepth,
	isDescendant,
	mapTree,
	removeCard,
	subtreeHeight,
} from './tree'

export type DropZone =
	| { kind: 'root'; where: 'canvas' | 'someday' }
	| {
			kind: 'before' | 'after' | 'into'
			where: 'canvas' | 'someday'
			targetId: string
	  }

export type WritingMode = 'cards' | 'document'
export type Orientation = 'vertical' | 'horizontal'

export function usePrototypeState() {
	const [canvas, setCanvas] = useState<ProtoCard[]>(() => seedCanvas())
	const [someday, setSomeday] = useState<ProtoCard[]>(() => seedSomeday())
	const [mode, setMode] = useState<WritingMode>('cards')
	const [docText, setDocText] = useState('')
	const [orientation, setOrientation] = useState<Orientation>('vertical')
	const [openBackId, setOpenBackId] = useState<string | null>(null)
	const [selectedId, setSelectedId] = useState<string | null>(null)
	const [modal, setModal] = useState<null | 'convert' | 'export'>(null)
	const [exportTaggedOnly, setExportTaggedOnly] = useState(false)
	const [exportFormat, setExportFormat] = useState<'pdf' | 'rtf'>('pdf')
	const [searchText, setSearchText] = useState('')
	const [labelPills, setLabelPills] = useState<string[]>([])
	const [authorPills, setAuthorPills] = useState<string[]>([])
	const [toast, setToast] = useState<string | null>(null)
	const [debugTick, setDebugTick] = useState(0)
	const [collapsedIds, setCollapsedIds] = useState<Record<string, boolean>>({})
	const [draggingId, setDraggingId] = useState<string | null>(null)
	const [draggingWhere, setDraggingWhere] = useState<
		'canvas' | 'someday' | null
	>(null)
	const [dropPreview, setDropPreview] = useState<DropZone | null>(null)
	const [dragCursor, setDragCursor] = useState<{ x: number; y: number } | null>(
		null,
	)
	/** Compact outline while dragging; brief hold after drop so list can expand back. */
	const [listMode, setListMode] = useState<'cards' | 'outline'>('cards')
	const listModeTimer = useRef<number | null>(null)
	/** Gap under the pointer — press `n` to insert a card here (canvas only). */
	const [hoverInsert, setHoverInsert] = useState<DropZone | null>(null)
	/** Focus title input + scroll into view after create/insert. */
	const [focusTitleId, setFocusTitleId] = useState<string | null>(null)
	/** Brief highlight / enter animation for a newly inserted card. */
	const [justInsertedId, setJustInsertedId] = useState<string | null>(null)
	const justInsertedTimer = useRef<number | null>(null)

	const markJustInserted = (id: string) => {
		if (justInsertedTimer.current != null) {
			window.clearTimeout(justInsertedTimer.current)
		}
		setJustInsertedId(id)
		justInsertedTimer.current = window.setTimeout(() => {
			setJustInsertedId(null)
			justInsertedTimer.current = null
		}, 450)
	}

	const enterOutlineMode = () => {
		if (listModeTimer.current != null) {
			window.clearTimeout(listModeTimer.current)
			listModeTimer.current = null
		}
		setListMode('outline')
	}

	const scheduleCardsMode = () => {
		if (listModeTimer.current != null) {
			window.clearTimeout(listModeTimer.current)
		}
		listModeTimer.current = window.setTimeout(() => {
			setListMode('cards')
			listModeTimer.current = null
		}, 320)
	}

	useEffect(() => {
		return () => {
			if (listModeTimer.current != null) {
				window.clearTimeout(listModeTimer.current)
			}
			if (justInsertedTimer.current != null) {
				window.clearTimeout(justInsertedTimer.current)
			}
		}
	}, [])

	const insertCardAt = (zone: DropZone): string | null => {
		if (zone.where !== 'canvas' || mode !== 'cards') return null
		const node = emptyProtoCard()
		let ok = false
		setCanvas((prev) => {
			const next = structuredClone(prev) as ProtoCard[]
			if (zone.kind === 'root') {
				next.push(node)
				ok = true
				return next
			}
			const target = findCard(next, zone.targetId)
			if (!target) return prev
			if (zone.kind === 'before') {
				target.parent.splice(target.index, 0, node)
				ok = true
			} else if (zone.kind === 'after') {
				target.parent.splice(target.index + 1, 0, node)
				ok = true
			} else if (zone.kind === 'into') {
				if ((depthOf(next, zone.targetId) ?? 1) >= 3) {
					flash('Max depth is 3')
					return prev
				}
				target.card.children.push(node)
				setCollapsedIds((c) => ({ ...c, [zone.targetId]: false }))
				ok = true
			}
			return ok ? next : prev
		})
		if (!ok) return null
		setOpenBackId(null)
		setSelectedId(node.id)
		setFocusTitleId(node.id)
		markJustInserted(node.id)
		setHoverInsert(null)
		bump()
		return node.id
	}

	const addCardAtEnd = () => {
		return insertCardAt({ kind: 'root', where: 'canvas' })
	}

	const clearFocusTitle = () => setFocusTitleId(null)

	const flash = (msg: string) => {
		setToast(msg)
		window.setTimeout(() => setToast(null), 2200)
	}

	const docIsEmpty = !docText.trim()
	const cardsDisabled = mode === 'document' && !docIsEmpty

	const filteredSomeday = useMemo(
		() =>
			filterSomedayTree(someday, {
				text: searchText,
				labelPills,
				authorPills,
			}),
		[someday, searchText, labelPills, authorPills],
	)

	const labelOptions = useMemo(() => collectLabels(someday), [someday])
	const authorOptions = useMemo(() => collectAuthors(someday), [someday])

	const bump = () => setDebugTick((t) => t + 1)

	const togglePresentation = (id: string, where: 'canvas' | 'someday') => {
		const setter = where === 'canvas' ? setCanvas : setSomeday
		setter((prev) =>
			mapTree(prev, (c) =>
				c.id === id
					? { ...c, presentationTagged: !c.presentationTagged }
					: c,
			),
		)
		bump()
	}

	const updateCard = (
		id: string,
		where: 'canvas' | 'someday',
		patch: Partial<ProtoCard>,
	) => {
		const setter = where === 'canvas' ? setCanvas : setSomeday
		setter((prev) =>
			mapTree(prev, (c) => (c.id === id ? { ...c, ...patch } : c)),
		)
		bump()
	}

	const moveToCanvas = (id: string) => {
		setSomeday((prev) => {
			const next = structuredClone(prev) as ProtoCard[]
			const removed = removeCard(next, id)
			if (!removed) return prev
			setCanvas((c) => [...c, removed])
			flash(`Moved “${removed.faceTitle}” onto canvas`)
			bump()
			return next
		})
	}

	const moveToSomeday = (id: string) => {
		setCanvas((prev) => {
			const next = structuredClone(prev) as ProtoCard[]
			const removed = removeCard(next, id)
			if (!removed) return prev
			removed.authorId = CURRENT_USER.id
			removed.authorName = CURRENT_USER.name
			setSomeday((s) => [...s, removed])
			flash(`Moved “${removed.faceTitle}” to Someday`)
			bump()
			return next
		})
	}

	const copyToCanvas = (id: string) => {
		const hit = findCard(someday, id)
		if (!hit) return
		const copy = cloneTree([hit.card])[0]!
		copy.authorId = CURRENT_USER.id
		copy.authorName = CURRENT_USER.name
		setCanvas((c) => [...c, copy])
		flash(`Copied “${hit.card.faceTitle}” to canvas`)
		bump()
	}

	const copyToSomeday = (id: string) => {
		const hit = findCard(canvas, id)
		if (!hit) return
		const copy = cloneTree([hit.card])[0]!
		copy.authorId = CURRENT_USER.id
		copy.authorName = CURRENT_USER.name
		setSomeday((s) => [...s, copy])
		flash(`Copied “${hit.card.faceTitle}” to Someday`)
		bump()
	}

	const startNewSermon = (id: string, asCopy: boolean) => {
		const hit = findCard(someday, id)
		if (!hit) return
		const tree = asCopy ? cloneTree([hit.card]) : [structuredClone(hit.card)]
		if (!asCopy) {
			setSomeday((prev) => {
				const next = structuredClone(prev) as ProtoCard[]
				removeCard(next, id)
				return next
			})
		}
		setCanvas(tree)
		setMode('cards')
		setDocText('')
		flash(
			asCopy
				? `New sermon (copy): “${hit.card.faceTitle}”`
				: `New sermon (moved): “${hit.card.faceTitle}”`,
		)
		bump()
	}

	const moveSibling = (id: string, dir: -1 | 1) => {
		setCanvas((prev) => {
			const next = structuredClone(prev) as ProtoCard[]
			const hit = findCard(next, id)
			if (!hit) return prev
			const j = hit.index + dir
			if (j < 0 || j >= hit.parent.length) return prev
			const tmp = hit.parent[hit.index]!
			hit.parent[hit.index] = hit.parent[j]!
			hit.parent[j] = tmp
			bump()
			return next
		})
	}

	const indent = (id: string) => {
		setCanvas((prev) => {
			const next = structuredClone(prev) as ProtoCard[]
			const hit = findCard(next, id)
			if (!hit || hit.index === 0) return prev
			const flat = flattenWithDepth(next)
			const self = flat.find((f) => f.card.id === id)
			if (!self || self.depth >= 3) {
				flash('Max depth is 3')
				return prev
			}
			const prevSibling = hit.parent[hit.index - 1]!
			const [node] = hit.parent.splice(hit.index, 1)
			if (!node) return prev
			prevSibling.children.push(node)
			bump()
			return next
		})
	}

	const outdent = (id: string) => {
		setCanvas((prev) => {
			const next = structuredClone(prev) as ProtoCard[]
			// Find parent of parent
			const walk = (
				list: ProtoCard[],
				parentList: ProtoCard[] | null,
				parent: ProtoCard | null,
			): boolean => {
				for (let i = 0; i < list.length; i++) {
					const c = list[i]!
					if (c.id === id) {
						if (!parent || !parentList) return false
						const [node] = list.splice(i, 1)
						if (!node) return false
						const pIndex = parentList.indexOf(parent)
						parentList.splice(pIndex + 1, 0, node)
						return true
					}
					if (walk(c.children, list, c)) return true
				}
				return false
			}
			if (!walk(next, null, null)) return prev
			bump()
			return next
		})
	}

	const trySetMode = (next: WritingMode) => {
		if (next === 'cards' && cardsDisabled) {
			flash('Clear the document to switch to Cards')
			return
		}
		setMode(next)
		bump()
	}

	const toggleCollapsed = (id: string) => {
		setCollapsedIds((prev) => ({ ...prev, [id]: !prev[id] }))
	}

	const isCollapsed = (id: string) => Boolean(collapsedIds[id])

	const clearDrag = () => {
		setDraggingId(null)
		setDraggingWhere(null)
		setDropPreview(null)
		setDragCursor(null)
		scheduleCardsMode()
	}

	const beginDrag = (id: string, where: 'canvas' | 'someday') => {
		setHoverInsert(null)
		enterOutlineMode()
		setDraggingId(id)
		setDraggingWhere(where)
	}

	const findDraggingCard = (): ProtoCard | null => {
		if (!draggingId) return null
		return (
			findCard(canvas, draggingId)?.card ??
			findCard(someday, draggingId)?.card ??
			null
		)
	}

	const handleDrop = (
		sourceId: string,
		sourceWhere: 'canvas' | 'someday',
		zone: DropZone,
	) => {
		if (zone.kind !== 'root' && zone.targetId === sourceId) {
			clearDrag()
			return
		}

		// Optimistic: commit tree move + clear drag in one React batch so the card
		// appears in the new spot without snapping back to the old place first.
		let nextCanvas = structuredClone(canvas) as ProtoCard[]
		let nextSomeday = structuredClone(someday) as ProtoCard[]
		const fromList = sourceWhere === 'canvas' ? nextCanvas : nextSomeday
		const node = removeCard(fromList, sourceId)
		if (!node) {
			clearDrag()
			return
		}

		const destWhere = zone.where
		const toList = destWhere === 'canvas' ? nextCanvas : nextSomeday
		let ok = true
		let err: string | null = null

		if (zone.kind === 'root') {
			toList.push(node)
		} else {
			const target = findCard(toList, zone.targetId)
			if (!target) {
				ok = false
				err = 'Drop target not found'
			} else if (zone.kind === 'into') {
				if (isDescendant(node, zone.targetId)) {
					ok = false
					err = 'Can’t nest a card inside itself'
				} else if (
					(depthOf(toList, zone.targetId) ?? 1) + subtreeHeight(node) >
					3
				) {
					ok = false
					err = 'Max depth is 3'
				} else {
					target.card.children.push(node)
					setCollapsedIds((prev) => ({ ...prev, [zone.targetId]: false }))
				}
			} else if (zone.kind === 'before') {
				target.parent.splice(target.index, 0, node)
			} else {
				target.parent.splice(target.index + 1, 0, node)
			}
		}

		if (!ok) {
			clearDrag()
			if (err) flash(err)
			return
		}

		if (destWhere === 'someday' && sourceWhere === 'canvas') {
			node.authorId = CURRENT_USER.id
			node.authorName = CURRENT_USER.name
		}

		setCanvas(nextCanvas)
		setSomeday(nextSomeday)
		setDraggingId(null)
		setDraggingWhere(null)
		setDropPreview(null)
		setDragCursor(null)
		scheduleCardsMode()
		bump()
	}

	const confirmConvert = useCallback(() => {
		const lines: string[] = ['I Have a Dream (converted)', '']
		const walk = (list: ProtoCard[], depth: number) => {
			for (const c of list) {
				lines.push(`${'#'.repeat(depth)} ${c.faceTitle}`)
				if (c.faceSummary) lines.push(c.faceSummary)
				lines.push(c.back)
				lines.push('')
				walk(c.children, depth + 1)
			}
		}
		walk(canvas, 1)
		setDocText(lines.join('\n'))
		setCanvas([])
		setMode('document')
		setModal(null)
		flash('Converted to document (prototype — in memory only)')
		bump()
	}, [canvas])

	const debugState = {
		mode,
		orientation,
		docIsEmpty,
		cardsDisabled,
		canvasCount: flattenWithDepth(canvas).length,
		somedayCount: flattenWithDepth(someday).length,
		selectedId,
		openBackId,
		draggingId,
		dropPreview,
		labelPills,
		authorPills,
		searchText,
		debugTick,
	}

	return {
		canvas,
		someday: filteredSomeday,
		rawSomeday: someday,
		mode,
		docText,
		setDocText,
		orientation,
		setOrientation,
		openBackId,
		setOpenBackId,
		selectedId,
		setSelectedId,
		modal,
		setModal,
		exportTaggedOnly,
		setExportTaggedOnly,
		exportFormat,
		setExportFormat,
		searchText,
		setSearchText,
		labelPills,
		setLabelPills,
		authorPills,
		setAuthorPills,
		labelOptions,
		authorOptions,
		toast,
		docIsEmpty,
		cardsDisabled,
		draggingId,
		draggingWhere,
		listMode,
		hoverInsert,
		setHoverInsert,
		focusTitleId,
		clearFocusTitle,
		justInsertedId,
		addCardAtEnd,
		insertCardAt,
		setDraggingId,
		beginDrag,
		dropPreview,
		setDropPreview,
		dragCursor,
		setDragCursor,
		clearDrag,
		findDraggingCard,
		trySetMode,
		togglePresentation,
		updateCard,
		moveToCanvas,
		moveToSomeday,
		copyToCanvas,
		copyToSomeday,
		startNewSermon,
		moveSibling,
		indent,
		outdent,
		toggleCollapsed,
		isCollapsed,
		handleDrop,
		confirmConvert,
		flash,
		debugState,
	}
}

export type PrototypeState = ReturnType<typeof usePrototypeState>
